import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GitBranch, Sparkles } from 'lucide-react';
import { AppBrandLogo } from './AppBrandLogo';
import { AppReleaseHistoryModal } from './AppReleaseHistoryModal';
import { appReleaseService } from '../../services/appReleaseService';

export interface SiteFooterProps {
  onOpenStaticPage?: (key: 'About' | 'Contact' | 'Privacy' | 'Terms') => void;
  onAdminLoginClick?: () => void;
  /** Version journey is only shown for signed-in users. */
  showVersionJourney?: boolean;
  className?: string;
  /** Learner dashboard: same circular mark as the header logo, with less space above. */
  matchHeaderLogo?: boolean;
}

/**
 * Shared site footer with clear separation from main content (gradient band + inset panel).
 */
export const SiteFooter: React.FC<SiteFooterProps> = ({
  onOpenStaticPage,
  onAdminLoginClick,
  showVersionJourney = false,
  className = '',
  matchHeaderLogo = false,
}) => {
  const { t } = useTranslation('nav');
  const { t: tCommon } = useTranslation('common');
  const [journeyOpen, setJourneyOpen] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void appReleaseService.getPublishedReleases(1).then((releases) => {
      if (!cancelled && releases[0]) {
        setLatestVersion(releases[0].versionLabel);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <footer className={`relative isolate ${className}`}>
        <div
          className="h-[2px] w-full bg-gradient-to-r from-transparent via-amber-400/70 to-transparent dark:via-amber-500/50"
          aria-hidden
        />
        <div className="relative overflow-hidden border-t border-slate-200/90 bg-gradient-to-b from-slate-100 via-slate-50 to-slate-100 dark:border-slate-800/90 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(245,158,11,0.12),transparent_55%)] dark:bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(245,158,11,0.08),transparent_55%)]"
            aria-hidden
          />
          <div className={`relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8 ${matchHeaderLogo ? 'pb-4 pt-2' : 'py-8'}`}>
            <div className={`mx-auto max-w-3xl rounded-2xl border border-slate-200/80 bg-white/70 px-5 shadow-lg shadow-slate-900/5 backdrop-blur-sm dark:border-slate-700/80 dark:bg-gray-900/60 dark:shadow-black/40 ${matchHeaderLogo ? 'py-4' : 'py-6'}`}>
              <div className={`flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-4 ${matchHeaderLogo ? 'mb-2' : 'mb-4'}`}>
                {matchHeaderLogo ? (
                  <span className="isolate flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white/30 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.2),rgba(255,255,255,0.1)),#16324f] shadow-lg ring-2 ring-amber-400/25 backdrop-blur-md">
                    <AppBrandLogo onCircle className="h-[3.625rem] w-[3.625rem]" alt="" />
                  </span>
                ) : (
                  <AppBrandLogo className="h-14 w-14 sm:h-16 sm:w-16" alt="" />
                )}
                <div className="text-center sm:text-left">
                  <h3 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-gray-100">
                    {tCommon('appName')}
                  </h3>
                  {latestVersion && (
                    <p className="mt-0.5 text-[11px] font-medium tracking-wide text-slate-500 dark:text-slate-400">
                      {t('footerVersion', { label: latestVersion })}
                    </p>
                  )}
                </div>
              </div>
              <p className="mx-auto mb-5 max-w-lg text-sm leading-relaxed text-slate-600 dark:text-slate-300 sm:text-base">
                {t('footerTagline')}
              </p>
              <nav
                className="mb-6 flex flex-wrap items-center justify-center gap-x-1 gap-y-1 text-sm font-medium text-slate-600 dark:text-slate-400"
                aria-label="Footer"
              >
                <button
                  type="button"
                  onClick={() => onOpenStaticPage?.('About')}
                  className="rounded-md px-1 transition-colors hover:text-amber-800 dark:hover:text-amber-300"
                >
                  {t('about')}
                </button>
                <span className="select-none text-slate-300 dark:text-slate-600" aria-hidden>
                  |
                </span>
                <button
                  type="button"
                  onClick={() => onOpenStaticPage?.('Contact')}
                  className="rounded-md px-1 transition-colors hover:text-amber-800 dark:hover:text-amber-300"
                >
                  {t('contact')}
                </button>
                <span className="select-none text-slate-300 dark:text-slate-600" aria-hidden>
                  |
                </span>
                <button
                  type="button"
                  onClick={() => onOpenStaticPage?.('Privacy')}
                  className="rounded-md px-1 transition-colors hover:text-amber-800 dark:hover:text-amber-300"
                >
                  {t('privacy')}
                </button>
                <span className="select-none text-slate-300 dark:text-slate-600" aria-hidden>
                  |
                </span>
                <button
                  type="button"
                  onClick={() => onOpenStaticPage?.('Terms')}
                  className="rounded-md px-1 transition-colors hover:text-amber-800 dark:hover:text-amber-300"
                >
                  {t('terms')}
                </button>
              </nav>

              {showVersionJourney && (
                <div className="mt-2 flex justify-center pt-1">
                  <button
                    type="button"
                    data-tour-id="footer-version-journey"
                    onClick={() => setJourneyOpen(true)}
                    className="group inline-flex items-center gap-2.5 rounded-full border border-amber-300/70 bg-gradient-to-r from-amber-50 via-white to-orange-50/90 px-5 py-3 text-sm font-semibold text-amber-950 shadow-sm shadow-amber-900/5 transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-md hover:shadow-amber-900/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 dark:border-amber-700/50 dark:from-amber-950/40 dark:via-gray-900 dark:to-orange-950/30 dark:text-amber-100 dark:hover:border-amber-500/60"
                    aria-haspopup="dialog"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md shadow-amber-600/30 transition-transform duration-200 group-hover:scale-105">
                      <GitBranch className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="flex flex-col items-start text-start leading-tight">
                      <span className="flex items-center gap-1">
                        {t('versionJourney')}
                        <Sparkles className="h-3.5 w-3.5 text-amber-600 opacity-80 dark:text-amber-400" aria-hidden />
                      </span>
                      <span className="text-[10px] font-normal text-amber-900/70 dark:text-amber-200/70">
                        {t('versionJourneyFooterHint')}
                      </span>
                    </span>
                  </button>
                </div>
              )}

              {onAdminLoginClick && (
                <div className="mt-5 border-t border-slate-200/80 pt-4 dark:border-slate-700/80">
                  <button
                    type="button"
                    onClick={onAdminLoginClick}
                    className="text-xs text-slate-500 underline-offset-2 transition-colors hover:text-slate-800 hover:underline dark:text-slate-500 dark:hover:text-slate-300"
                  >
                    Admin Login
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>

      {showVersionJourney && (
        <AppReleaseHistoryModal open={journeyOpen} onClose={() => setJourneyOpen(false)} />
      )}
    </>
  );
};

export default SiteFooter;
